import { FallbackType, UserRole } from "../domain/auth.types";
import React, { JSX } from 'react';
import { getRoleLabel } from '../utils/role.utils';
import { useUserPermissions } from "@/features/auth/hooks/auth.hooks.ts";
import { FallbackContent } from "@/features/auth/components/ProtectedComponent/FallbackContent.tsx";

export interface ProtectedComponentProps {
  children: React.ReactNode;
  requiredRole?: UserRole;
  fallbackType?: FallbackType;
  fallbackTitle?: string;
  fallbackAction?: string;
  fallbackIcon?: React.ReactNode;
}

export function usePermissionCheck(requiredRole?: UserRole) {
  const permissions = useUserPermissions();

  if (!requiredRole) {
    return {hasPermission: true};
  }

  const hasPermission = permissions.hasRole(requiredRole);

  return {hasPermission};
}

/**
 * Component wrapper that conditionally renders children based on user permissions. We are not using MSAL's `<AuthenticatedTemplate>`
 * everywhere, because it doesn't allow us to check roles.
 */
export function ProtectedComponent({
                                     children,
                                     requiredRole,
                                     fallbackType,
                                     fallbackTitle,
                                     fallbackAction = 'access this feature',
                                     fallbackIcon
                                   }: ProtectedComponentProps): JSX.Element {
  const {hasPermission} = usePermissionCheck(requiredRole);

  if (hasPermission) {
    return <>{children}</>;
  }

  const roleLabel = getRoleLabel(requiredRole!);

  return (
    <>
      <FallbackContent
        type={fallbackType}
        title={fallbackTitle}
        action={fallbackAction}
        role={roleLabel}
        icon={fallbackIcon}
      />
    </>
  );
}